import React, { useState } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { UnifiedFeed } from '../components/social/UnifiedFeed';
import { SocialProvider } from '../contexts/SocialContext';
import CreatePostWithAI from '../components/social/CreatePostWithAI';
import PageHeader from '../components/layout/PageHeader';
import { AZURE } from '../services/api';
import { BRAND_GREEN, BRAND_GOLD, BRAND_GREEN_LIGHT } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';


// ===== Query Client =====
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

// ===== Brand tokens (match sidebar) =====
const BORDER = '#E5E7EB';

const FeedPage = () => {
  const { lang } = useSportsLang();
  const LABELS = {
    pt: {
      allSports: 'Todos os Esportes',
      subtitle: 'Onde o esporte acontece — e fica registrado.',
      badge: 'Feed Unificado',
      displayLabel: 'Exibir',
      quickFilters: 'Filtros rápidos:',
      all: 'Todos',
      publishTitle: 'Publicar no feed',
      publishDesc: 'Crie um post com ajuda do Arena Mind (texto, insights e cards).',
      updatesTitle: 'Atualizações',
      sortedByDate: 'Ordenado por data',
      fallbackSports: [
        { id: 'swimming', name: 'Natação', icon: '🏔', status: 'ready' },
        { id: 'football', name: 'Futebol', icon: '⚽', status: 'ready' },
        { id: 'basketball', name: 'Basquete', icon: '🏀', status: 'pending' },
        { id: 'volleyball', name: 'Vôlei', icon: '🏐', status: 'pending' },
        { id: 'athletics', name: 'Atletismo', icon: '🏃', status: 'pending' },
      ],
    },
    en: {
      allSports: 'All Sports',
      subtitle: 'Where sport happens — and gets recorded.',
      badge: 'Unified Feed',
      displayLabel: 'Display',
      quickFilters: 'Quick filters:',
      all: 'All',
      publishTitle: 'Publish to feed',
      publishDesc: 'Create a post with the help of Arena Mind (text, insights and cards).',
      updatesTitle: 'Updates',
      sortedByDate: 'Sorted by date',
      fallbackSports: [
        { id: 'swimming', name: 'Swimming', icon: '🏔', status: 'ready' },
        { id: 'football', name: 'Football', icon: '⚽', status: 'ready' },
        { id: 'basketball', name: 'Basketball', icon: '🏀', status: 'pending' },
        { id: 'volleyball', name: 'Volleyball', icon: '🏐', status: 'pending' },
        { id: 'athletics', name: 'Athletics', icon: '🏃', status: 'pending' },
      ],
    },
  };
  const T = LABELS[lang] || LABELS.pt;

  const [selectedSports, setSelectedSports] = useState(['swimming', 'football']);

  // ===== MUDANÇA: Buscar esportes dinamicamente via Orchestrator Agent =====
  const { data: sportsData, isLoading: loadingSports } = useQuery({
    queryKey: ['sports-metadata'],
    queryFn: async () => {
      try {
        const { data } = await AZURE.get('/orchestrator/discover-sports');
        return data.sports || [];
      } catch (error) {
        console.error('[FeedPage] Erro ao buscar esportes:', error);
        // Fallback para lista hardcoded se API falhar
        return T.fallbackSports;
      }
    },
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // ===== MUDANÇA: Buscar status de sincronização (live widget) =====
  const { data: syncStatus } = useQuery({
    queryKey: ['sync-status'],
    queryFn: async () => {
      try {
        const { data } = await AZURE.get('/orchestrator/sync-status');
        return data.sync_status || {};
      } catch (error) {
        console.error('[FeedPage] Erro ao buscar sync status:', error);
        return {};
      }
    },
    staleTime: 30 * 1000, // Atualiza a cada 30 segundos
    refetchInterval: 30 * 1000, // Polling automático
  });

  const sports = [
    { id: 'all', name: T.allSports, icon: '🏟️' },
    ...(sportsData || []),
  ];

  const handleSportChange = (e) => {
    const value = e.target.value;
    if (value === 'all') {
      setSelectedSports(sports.filter((s) => s.id !== 'all').map((s) => s.id));
    } else {
      setSelectedSports([value]);
    }
  };

  const selectedValue = selectedSports.length === 1 ? selectedSports[0] : 'all';

  return (
    <QueryClientProvider client={queryClient}>
      <SocialProvider>
        {/* Page background slate-50 (#f8fafc) - Design System */}
        <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
          {/* Container width matches your 6xl standard */}
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-5 md:py-7">

            {/* Header Oficial */}
            <PageHeader
              title="Arena Stats"
              subtitle={
                <>
                  {T.subtitle}
                  {/* Widget de status de sincronização (live) */}
                  {syncStatus?.football?.leagues?.find(l => l.status === 'syncing') && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span>
                        📊 Sincronizando {syncStatus.football.leagues[0].league_name}...{' '}
                        {syncStatus.football.leagues[0].processed_matches}/{syncStatus.football.leagues[0].total_matches} partidas
                        {syncStatus.football.quota_used && (
                          <span className="ml-2 text-gray-400">
                            ({syncStatus.football.quota_used}/{syncStatus.football.quota_total} quota)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </>
              }
              badgeText={T.badge}
              badgeIcon={true}
              rightSlot={
                <div className="flex flex-col items-end gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    {T.displayLabel}
                  </span>
                  <select
                    value={selectedValue}
                    onChange={handleSportChange}
                    className="px-3 py-2 text-sm rounded-xl border bg-white text-gray-700 focus:outline-none focus:ring-2 transition-all"
                    style={{
                      borderColor: '#E5E7EB',
                      '--tw-ring-color': '#f3f4f6',
                    }}
                  >
                    {sports.map((sport) => (
                      <option key={sport.id} value={sport.id}>
                        {sport.name}
                      </option>
                    ))}
                  </select>
                </div>
              }
              secondaryContent={
                <div className="rounded-xl border bg-gray-50/40 overflow-hidden" style={{ borderColor: '#E5E7EB' }}>
                  <div className="flex items-center">
                    <div className="w-1 self-stretch" style={{ backgroundColor: BRAND_GOLD }} />
                    <div className="px-4 py-3 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold" style={{ color: BRAND_GREEN }}>
                        {T.quickFilters}
                      </span>

                      {/* Quick chips */}
                      {sports
                        .filter((s) => s.id !== 'all')
                        .slice(0, 4)
                        .map((s) => {
                          const active =
                            selectedSports.length === 1
                              ? selectedSports[0] === s.id
                              : selectedSports.includes(s.id);

                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => setSelectedSports([s.id])}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                active ? 'bg-green-50' : 'bg-white hover:bg-gray-50'
                              }`}
                              style={{
                                borderColor: active ? BRAND_GOLD : '#E5E7EB',
                                color: BRAND_GREEN,
                              }}
                            >
                              <span className="mr-1">{s.icon}</span>
                              {s.name}
                            </button>
                          );
                        })}

                      <button
                        type="button"
                        onClick={() => setSelectedSports(sports.filter((s) => s.id !== 'all').map((s) => s.id))}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-white hover:bg-gray-50 transition-all"
                        style={{ borderColor: '#E5E7EB', color: BRAND_GREEN }}
                      >
                        {T.all}
                      </button>
                    </div>
                  </div>
                </div>
              }
            />

            {/* Create Post Card (white card, gold accent) */}
            <div className="mt-6">
              <div
                className="bg-white rounded-2xl border overflow-hidden"
                style={{ 
                  borderColor: BORDER,
                  boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)'
                }}
              >
                <div className="flex items-center">
                  <div className="w-1 self-stretch" style={{ backgroundColor: BRAND_GOLD }} />
                  <div className="p-5 md:p-6 w-full">
                    {/* Optional header for consistency */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-sm font-extrabold" style={{ color: BRAND_GREEN }}>
                          {T.publishTitle}
                        </h2>
                        <p className="text-xs text-gray-500">
                          {T.publishDesc}
                        </p>
                      </div>
                      <span
                        className="text-[10px] font-bold text-white px-2 py-1 rounded"
                        style={{ backgroundColor: BRAND_GOLD }}
                      >
                        AI
                      </span>
                    </div>

                    <CreatePostWithAI />
                  </div>
                </div>
              </div>
            </div>

            {/* Feed */}
            <div className="mt-6">
              <div
                className="bg-white rounded-2xl border"
                style={{ 
                  borderColor: BORDER,
                  boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)'
                }}
              >
                <div className="p-5 md:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-extrabold" style={{ color: BRAND_GREEN }}>
                      {T.updatesTitle}
                    </h2>
                    <span className="text-xs text-gray-400">
                      {T.sortedByDate}
                    </span>
                  </div>

                  <UnifiedFeed sports={selectedSports} sortBy="date" search="" />
                </div>
              </div>
            </div>

            {/* bottom spacing */}
            <div className="h-8" />
          </div>
        </div>
      </SocialProvider>
    </QueryClientProvider>
  );
};

export default FeedPage;
